import React, {
  FC,
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import cn from "clsx";
import { useTranslation } from "react-i18next";

import Dropdown, { DropdownItemProps } from "@/components/dropdown/dropdown";
import { useSizeFilterHelpers } from "../../hooks/useSizeFilterHelpers";
import { SelectedFiltersContext } from "../../webFilteringSection";
import FilterElement from "../filterElement";
import FilterSizeSwitcher from "./filterSizeSwitcher";
import {
  checkIfActive,
  collectAllSizes,
  collectLeafKeysForMode,
  getKey
} from "./utils";
import {
  FilterSizeVariants,
  GeneralFacet,
  SingleSize,
  SizeCategory
} from "../../types";
import {
  filterSwitcherTextVariants,
  resolveAdvancedData,
  resolveSimpleData
} from "../../utils";

import "./style.scss";

type Mode = FilterSizeVariants.SIMPLE | FilterSizeVariants.ADVANCED;

type Props = {
  filter: GeneralFacet & { data: SizeCategory[] };
};

const hasChildren = (node: any) => node?.children?.length > 0;

const countSelectedInNode = (node: any): number => {
  if (!node) return 0;

  if (Array.isArray(node)) {
    return node.reduce((sum, item) => sum + countSelectedInNode(item), 0);
  }

  if (hasChildren(node)) {
    return node.children.reduce(
      (sum: number, child: any) => sum + countSelectedInNode(child),
      0
    );
  }

  return node?.isSelected ? 1 : 0;
};

const useCategories = (filter: Props["filter"]) => {
  const categories = useMemo(
    () => (Array.isArray(filter?.data) ? filter.data : []),
    [filter]
  );

  const [activeKey, setActiveKey] = useState<string | null>(
    categories.length ? getKey(categories[0], "category-0") : null
  );

  useEffect(() => {
    if (!categories.length) {
      setActiveKey(null);
      return;
    }
    const exists = categories.some(
      (cat, idx) => getKey(cat, `category-${idx}`) === activeKey
    );
    if (!exists) {
      setActiveKey(getKey(categories[0], "category-0"));
    }
  }, [categories, activeKey]);

  const activeCategory = useMemo(() => {
    if (!categories.length) return null;
    return (
      categories.find(
        (cat, idx) => getKey(cat, `category-${idx}`) === activeKey
      ) || categories[0]
    );
  }, [categories, activeKey]);

  return { categories, activeCategory, activeKey, setActiveKey };
};

const useGroups = (
  mode: Mode,
  activeCategory: SizeCategory | null,
  activeCategoryKey: string | null,
  skipNextGroupResetRef: MutableRefObject<boolean>
) => {
  const simpleGroups = useMemo(
    () => resolveSimpleData(activeCategory?.data) || [],
    [activeCategory]
  );
  const advancedGroups = useMemo(
    () => resolveAdvancedData(activeCategory?.data) || [],
    [activeCategory]
  );

  const groups =
    mode === FilterSizeVariants.SIMPLE ? simpleGroups : advancedGroups;

  const [activeKey, setActiveKey] = useState<string | null>(
    groups?.length ? getKey(groups[0], "group-0") : null
  );

  const prevCategoryKeyRef = useRef<string | null>(activeCategoryKey);
  const prevModeRef = useRef(mode);

  useEffect(() => {
    const categoryChanged = prevCategoryKeyRef.current !== activeCategoryKey;
    const modeChanged = prevModeRef.current !== mode;
    prevCategoryKeyRef.current = activeCategoryKey;
    prevModeRef.current = mode;

    if (!groups?.length) {
      setActiveKey(null);
      return;
    }

    if (categoryChanged || modeChanged) {
      if (skipNextGroupResetRef.current) {
        skipNextGroupResetRef.current = false;
        return;
      }
      setActiveKey(getKey(groups[0], "group-0"));
      return;
    }

    const exists = groups.some(
      (group, idx) => getKey(group, `group-${idx}`) === activeKey
    );
    if (!exists) {
      setActiveKey(getKey(groups[0], "group-0"));
    }
  }, [groups, mode, activeCategoryKey, activeKey, skipNextGroupResetRef]);

  const activeGroup = useMemo(() => {
    if (!groups?.length) return null;
    return (
      groups.find(
        (group, idx) => getKey(group, `group-${idx}`) === activeKey
      ) || groups[0]
    );
  }, [groups, activeKey]);

  return { groups, activeGroup, activeKey, setActiveKey };
};

const useAdvancedSelectors = (mode: Mode, activeGroup: any) => {
  const styles =
    mode === FilterSizeVariants.ADVANCED ? activeGroup?.children || [] : [];
  const [styleKey, setStyleKey] = useState<string | null>(
    styles?.length ? getKey(styles[0], "style-0") : null
  );

  useEffect(() => {
    if (!styles?.length) {
      setStyleKey(null);
      return;
    }
    const exists = styles.some(
      (style, idx) => getKey(style, `style-${idx}`) === styleKey
    );
    if (!exists) {
      setStyleKey(getKey(styles[0], "style-0"));
    }
  }, [styles, styleKey]);

  const activeStyle = useMemo(() => {
    if (!styles?.length) return null;
    return (
      styles.find((style, idx) => getKey(style, `style-${idx}`) === styleKey) ||
      styles[0]
    );
  }, [styles, styleKey]);

  const formats =
    mode === FilterSizeVariants.ADVANCED ? activeStyle?.children || [] : [];

  const [formatKey, setFormatKey] = useState<string | null>(
    formats?.length ? getKey(formats[0], "format-0") : null
  );

  useEffect(() => {
    if (!formats?.length) {
      setFormatKey(null);
      return;
    }
    const exists = formats.some(
      (format, idx) => getKey(format, `format-${idx}`) === formatKey
    );
    if (!exists) {
      setFormatKey(getKey(formats[0], "format-0"));
    }
  }, [formats, formatKey]);

  const activeFormat = useMemo(() => {
    if (!formats?.length) return null;
    return (
      formats.find(
        (format, idx) => getKey(format, `format-${idx}`) === formatKey
      ) || formats[0]
    );
  }, [formats, formatKey]);

  return {
    styles,
    activeStyleKey: styleKey,
    setActiveStyleKey: setStyleKey,
    formats,
    activeFormatKey: formatKey,
    setActiveFormatKey: setFormatKey,
    activeFormat
  };
};

const useSelectedSizes = (
  filter: Props["filter"],
  categories: SizeCategory[],
  selectedFilters: any,
  onSelection: any
) => {
  const preset = selectedFilters?.[filter.queryParam]?.selectedOptions;
  const [selectedSizes, setSelectedSizes] = useState<SingleSize[]>(
    Array.isArray(preset)
      ? (preset as SingleSize[])
      : collectAllSizes(categories).filter(size => size?.isSelected)
  );

  useEffect(() => {
    const ctx = selectedFilters?.[filter.queryParam]?.selectedOptions;
    if (Array.isArray(ctx)) {
      setSelectedSizes(ctx as SingleSize[]);
      return;
    }
    setSelectedSizes(
      collectAllSizes(categories).filter(size => size?.isSelected)
    );
  }, [selectedFilters, filter.queryParam, categories]);

  const toggleSize = useCallback(
    (size: SingleSize) => {
      setSelectedSizes(prev => {
        const exists = prev.some(item => item.key === size.key);
        const updated = exists
          ? prev.filter(item => item.key !== size.key)
          : [...prev, size];

        onSelection(
          {
            ...filter,
            selectedOptions: updated
          },
          selectedFilters
        );

        return updated;
      });
    },
    [filter, onSelection, selectedFilters]
  );

  const applySelection = useCallback(
    (sizes: SingleSize[]) => {
      setSelectedSizes(sizes);
      onSelection(
        {
          ...filter,
          selectedOptions: sizes
        },
        selectedFilters
      );
    },
    [filter, onSelection, selectedFilters]
  );

  return { selectedSizes, toggleSize, applySelection };
};

const FilterSizesAggregatedWeb: FC<Props> = ({ filter }) => {
  const { t } = useTranslation("productListing");
  const { onSelection, selectedFilters } = useContext(SelectedFiltersContext);
  const [mode, setMode] = useState<Mode>(FilterSizeVariants.SIMPLE);
  const [panelSubKeyByNode, setPanelSubKeyByNode] = useState<
    Record<string, string>
  >({});
  const forwardScrollStreak = useRef(0);
  const backwardScrollStreak = useRef(0);
  const categoryScrollerRef = useRef<HTMLDivElement>(null);
  const [isCategoryScrollerDragging, setIsCategoryScrollerDragging] =
    useState(false);
  const skipNextGroupResetRef = useRef(false);
  const prevModeForSelectionRef = useRef(mode);
  const selectedSizesRef = useRef<SingleSize[]>([]);

  const shouldFocusAfterAutoModeSwitchRef = useRef(false);
  const skipNextModeCleanupRef = useRef(false);
  const lastUserSelectedFormatByNodeRef = useRef<Record<string, string>>({});
  const skipNextPanelResetRef = useRef(false);

  const { findFirstSelectedOptionPath } = useSizeFilterHelpers();

  const {
    categories,
    activeCategory,
    activeKey: activeCategoryKey,
    setActiveKey: setActiveCategoryKey
  } = useCategories(filter);

  const {
    groups,
    activeGroup,
    activeKey: activeGroupKey,
    setActiveKey: setActiveGroupKey
  } = useGroups(mode, activeCategory, activeCategoryKey, skipNextGroupResetRef);

  const {
    styles,
    activeStyleKey,
    setActiveStyleKey,
    formats,
    activeFormatKey,
    setActiveFormatKey,
    activeFormat
  } = useAdvancedSelectors(mode, activeGroup);

  const visibleSizesAdvanced: SingleSize[] =
    (activeFormat?.children as SingleSize[]) || [];

  const { selectedSizes, toggleSize, applySelection } = useSelectedSizes(
    filter,
    categories,
    selectedFilters,
    onSelection
  );

  selectedSizesRef.current = selectedSizes;

  useEffect(() => {
    if (skipNextPanelResetRef.current) {
      skipNextPanelResetRef.current = false;
      setPanelSubKeyByNode(lastUserSelectedFormatByNodeRef.current);
      return;
    }
    setPanelSubKeyByNode({});
  }, [activeGroupKey]);

  useEffect(() => {
    const prev = prevModeForSelectionRef.current;
    if (prev === mode) return;

    const flipped =
      (prev === FilterSizeVariants.SIMPLE &&
        mode === FilterSizeVariants.ADVANCED) ||
      (prev === FilterSizeVariants.ADVANCED &&
        mode === FilterSizeVariants.SIMPLE);

    prevModeForSelectionRef.current = mode;

    if (!flipped) return;

    if (skipNextModeCleanupRef.current) {
      skipNextModeCleanupRef.current = false;
      return;
    }

    applySelection([]);
  }, [mode, categories, applySelection]);

  const categoryCounts = useMemo(() => {
    return categories?.reduce<Record<string, number>>((acc, category, idx) => {
      const key = getKey(category, `category-${idx}`);

      const dataByMode =
        mode === FilterSizeVariants.SIMPLE
          ? category?.data?.simple || []
          : category?.data?.advanced || [];

      acc[key] = countSelectedInNode(dataByMode);

      return acc;
    }, {});
  }, [categories, mode]);

  const groupCounts = useMemo(() => {
    const result: Record<string, number> = {};
    (groups ?? []).forEach((group: any, idx: number) => {
      const key = getKey(group, `group-${idx}`);
      result[key] = countSelectedInNode(group);
    });
    return result;
  }, [groups]);

  const styleCounts = useMemo(() => {
    const counts: { [key: string]: number } = {};

    (styles || []).forEach((style, idx) => {
      const key = getKey(style, `style-${idx}`);
      counts[key] = countSelectedInNode(style);
    });

    return counts;
  }, [styles]);

  const formatOptions = useMemo(
    () =>
      (formats || []).map(format => ({
        label: format.label,
        value: format.key
      })),
    [formats]
  );

  useEffect(() => {
    if (!formatOptions.length) return;
    const exists = formatOptions.some(opt => opt.value === activeFormatKey);
    if (!exists) {
      setActiveFormatKey(formatOptions[0].value);
    }
  }, [activeFormatKey, formatOptions]);

  const allCategories = useMemo(() => categories || [], [categories]);

  const getGroupsForCategory = useCallback(
    (category: SizeCategory | null, targetMode: Mode = mode) => {
      const resolver =
        targetMode === FilterSizeVariants.SIMPLE
          ? resolveSimpleData
          : resolveAdvancedData;
      return resolver(category?.data) || [];
    },
    [mode, resolveAdvancedData, resolveSimpleData]
  );

  const goToCategoryAndGroup = useCallback(
    (categoryIndex: number, groupIndex: number) => {
      const category = allCategories[categoryIndex];
      if (!category) return;
      const nextGroups = getGroupsForCategory(category);
      const nextGroup = nextGroups[groupIndex];
      if (!nextGroup) return;
      setActiveCategoryKey(getKey(category, `category-${categoryIndex}`));
      setActiveGroupKey(getKey(nextGroup, `group-${groupIndex}`));
    },
    [allCategories, getGroupsForCategory]
  );

  const goNext = useCallback(() => {
    const currentCategoryIndex = allCategories.findIndex(
      cat => getKey(cat, "").toString() === activeCategoryKey
    );
    const currentCategory =
      currentCategoryIndex >= 0
        ? allCategories[currentCategoryIndex]
        : allCategories[0];
    const nextGroups = getGroupsForCategory(currentCategory);
    const currentGroupIndex = nextGroups.findIndex(
      g => getKey(g, "") === activeGroupKey
    );

    if (currentGroupIndex === -1 && nextGroups.length) {
      setActiveGroupKey(getKey(nextGroups[0], "group-0"));
      return;
    }

    if (currentGroupIndex >= 0 && currentGroupIndex < nextGroups.length - 1) {
      const nextGroup = nextGroups[currentGroupIndex + 1];
      setActiveGroupKey(getKey(nextGroup, `group-${currentGroupIndex + 1}`));
      return;
    }

    if (currentCategoryIndex < allCategories.length - 1) {
      goToCategoryAndGroup(currentCategoryIndex + 1, 0);
    }
  }, [
    activeCategoryKey,
    activeGroupKey,
    allCategories,
    getGroupsForCategory,
    goToCategoryAndGroup
  ]);

  const goPrev = useCallback(() => {
    const currentCategoryIndex = allCategories.findIndex(
      cat => getKey(cat, "").toString() === activeCategoryKey
    );
    const currentCategory =
      currentCategoryIndex >= 0
        ? allCategories[currentCategoryIndex]
        : allCategories[allCategories.length - 1];
    const nextGroups = getGroupsForCategory(currentCategory);
    const currentGroupIndex = nextGroups.findIndex(
      g => getKey(g, "") === activeGroupKey
    );

    if (nextGroups.length) {
      if (currentGroupIndex <= 0) {
        if (currentCategoryIndex > 0) {
          const prevCategoryIndex = currentCategoryIndex - 1;
          const prevCategory = allCategories[prevCategoryIndex];
          const prevGroups = getGroupsForCategory(prevCategory);
          const lastIndex = Math.max(prevGroups.length - 1, 0);
          goToCategoryAndGroup(prevCategoryIndex, lastIndex);
          return;
        } else {
          setActiveGroupKey(getKey(nextGroups[0], "group-0"));
          return;
        }
      }

      if (currentGroupIndex > 0) {
        const prevGroup = nextGroups[currentGroupIndex - 1];
        setActiveGroupKey(getKey(prevGroup, `group-${currentGroupIndex - 1}`));
        return;
      }
    } else if (currentCategoryIndex > 0) {
      const prevCategoryIndex = currentCategoryIndex - 1;
      const prevCategory = allCategories[prevCategoryIndex];
      const prevGroups = getGroupsForCategory(prevCategory);
      const lastIndex = Math.max(prevGroups.length - 1, 0);
      goToCategoryAndGroup(prevCategoryIndex, lastIndex);
      return;
    }
  }, [
    activeCategoryKey,
    activeGroupKey,
    allCategories,
    getGroupsForCategory,
    goToCategoryAndGroup
  ]);

  const handleCategoryScrollerMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const el = categoryScrollerRef.current;
      if (!el || e.button !== 0) return;

      const startX = e.clientX;
      const scrollLeftStart = el.scrollLeft;
      let moved = false;

      const onMove = (moveEvent: MouseEvent) => {
        const dx = moveEvent.clientX - startX;
        if (Math.abs(dx) > 3) {
          moved = true;
          setIsCategoryScrollerDragging(true);
        }
        el.scrollLeft = scrollLeftStart - dx;
      };

      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        setIsCategoryScrollerDragging(false);
        if (moved) {
          const preventClick = (ev: MouseEvent) => {
            ev.preventDefault();
            ev.stopPropagation();
            document.removeEventListener("click", preventClick, true);
          };
          document.addEventListener("click", preventClick, true);
        }
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    []
  );

  const handleWheelNavigate = useCallback(
    (e: React.WheelEvent) => {
      const target = e.target as HTMLElement | null;
      const grid = target?.closest(".size_items_grid") as HTMLElement | null;
      if (grid) return;

      const menu = target?.closest(".size_menu") as HTMLElement | null;
      if (menu) return;

      const subnavRow = target?.closest(
        ".size_subnav_row"
      ) as HTMLElement | null;
      if (subnavRow) return;

      const dropdown = target?.closest(
        ".dropdownContainer"
      ) as HTMLElement | null;
      if (dropdown) return;

      e.preventDefault();
      e.stopPropagation();
      const threshold = 10;
      if (e.deltaY > 0) {
        forwardScrollStreak.current += 1;
        backwardScrollStreak.current = 0;
        if (forwardScrollStreak.current >= threshold) {
          forwardScrollStreak.current = 0;
          goNext();
        }
      } else if (e.deltaY < 0) {
        backwardScrollStreak.current += 1;
        forwardScrollStreak.current = 0;
        if (backwardScrollStreak.current >= threshold) {
          backwardScrollStreak.current = 0;
          goPrev();
        }
      }
    },
    [goNext, goPrev]
  );

  const resetSizeFilterNavigation = useCallback(() => {
    if (!categories.length) return;
    setActiveCategoryKey(getKey(categories[0], "category-0"));
    const firstCategory = categories[0];
    const firstGroups =
      mode === FilterSizeVariants.SIMPLE
        ? resolveSimpleData(firstCategory?.data) || []
        : resolveAdvancedData(firstCategory?.data) || [];
    if (firstGroups.length) {
      setActiveGroupKey(getKey(firstGroups[0], "group-0"));
    } else {
      setActiveGroupKey(null);
    }
  }, [categories, mode]);

  const focusFirstSelectedNavigation = useCallback(() => {
    const opts = selectedFilters?.[filter.queryParam]?.selectedOptions;
    const keys = Array.isArray(opts)
      ? opts.map((o: SingleSize) => o?.key).filter(Boolean)
      : [];
    if (!keys.length || !categories.length) return;

    const selectedKeySet = new Set(keys as string[]);

    let targetMode: Mode | null = null;

    for (let ci = 0; ci < categories.length; ci++) {
      const cat = categories[ci];

      const simpleGroups = resolveSimpleData(cat?.data) || [];
      for (let gi = 0; gi < simpleGroups.length; gi++) {
        const path = findFirstSelectedOptionPath(
          [simpleGroups[gi]],
          selectedKeySet
        );
        if (path) {
          targetMode = FilterSizeVariants.SIMPLE;
          break;
        }
      }

      if (targetMode) break;

      const advancedGroups = resolveAdvancedData(cat?.data) || [];
      for (let gi = 0; gi < advancedGroups.length; gi++) {
        const path = findFirstSelectedOptionPath(
          [advancedGroups[gi]],
          selectedKeySet
        );
        if (path) {
          targetMode = FilterSizeVariants.ADVANCED;
          break;
        }
      }

      if (targetMode) break;
    }

    if (targetMode && targetMode !== mode) {
      skipNextModeCleanupRef.current = true;
      shouldFocusAfterAutoModeSwitchRef.current = true;
      setMode(targetMode);
      return;
    }

    for (let ci = 0; ci < categories.length; ci++) {
      const cat = categories[ci];
      const groupsList =
        mode === FilterSizeVariants.SIMPLE
          ? resolveSimpleData(cat?.data) || []
          : resolveAdvancedData(cat?.data) || [];

      for (let gi = 0; gi < groupsList.length; gi++) {
        const path = findFirstSelectedOptionPath(
          [groupsList[gi]],
          selectedKeySet
        );
        if (!path) continue;

        skipNextGroupResetRef.current = true;
        if (Object.keys(lastUserSelectedFormatByNodeRef.current).length) {
          skipNextPanelResetRef.current = true;
        }
        setActiveCategoryKey(getKey(cat, `category-${ci}`));
        setActiveGroupKey(getKey(groupsList[gi], `group-${gi}`));

        if (mode === FilterSizeVariants.ADVANCED && path.tabKey) {
          const group = groupsList[gi];
          const styleChildren = group?.children || [];
          const si = styleChildren.findIndex(
            (s, i) =>
              getKey(s, `style-${i}`) === path.tabKey ||
              getKey(s, "") === path.tabKey
          );
          if (si >= 0) {
            setActiveStyleKey(getKey(styleChildren[si], `style-${si}`));
            const formatChildren = styleChildren[si]?.children || [];
            if (path.subgroupKey) {
              const fi = formatChildren.findIndex(
                (f, i) =>
                  getKey(f, `format-${i}`) === path.subgroupKey ||
                  getKey(f, "") === path.subgroupKey
              );
              if (fi >= 0) {
                setActiveFormatKey(getKey(formatChildren[fi], `format-${fi}`));
              }
            }
          }
        }
        return;
      }
    }
  }, [
    categories,
    mode,
    selectedFilters,
    filter.queryParam,
    findFirstSelectedOptionPath,
    setActiveCategoryKey,
    setActiveGroupKey,
    setActiveStyleKey,
    setActiveFormatKey
  ]);

  useEffect(() => {
    if (!shouldFocusAfterAutoModeSwitchRef.current) return;

    shouldFocusAfterAutoModeSwitchRef.current = false;
    focusFirstSelectedNavigation();
  }, [mode, focusFirstSelectedNavigation]);

  const availableSizeKeys = useMemo(() => {
    const keys = new Set<string>();
    collectAllSizes(categories).forEach(size => {
      if (size?.key && (size.count == null || Number(size.count) > 0)) {
        keys.add(size.key);
      }
    });
    return keys;
  }, [categories]);

  if (!categories.length) return null;

  const selectedSizesCount =
    selectedSizes?.filter(s => availableSizeKeys.has(s.key)).length || 0;

  const renderSizeChips = (sizes: SingleSize[]) =>
    sizes.map(size => (
      <button
        type="button"
        key={size.key}
        className={cn("size_chip", {
          active: checkIfActive(size, selectedSizes)
        })}
        onClick={() => toggleSize(size)}
      >
        {size.label}
      </button>
    ));

  const getLeaves = (nodes: any[]): SingleSize[] => {
    const result: SingleSize[] = [];
    for (const node of nodes) {
      if (node?.children?.length > 0) {
        result.push(...getLeaves(node.children));
      } else if (node) {
        result.push(node as SingleSize);
      }
    }
    return result;
  };

  const renderPanel = (node: any): React.ReactNode => {
    const children: any[] = node?.children || [];
    const layout: string | undefined = node?.childLayout;

    if (layout === "dropdown") {
      const childNodes = children.filter((c: any) => c.children?.length > 0);
      if (childNodes.length === 0) {
        return (
          <div className="size_items_grid">
            {renderSizeChips(getLeaves(children))}
          </div>
        );
      }
      const nodeKey = getKey(node, "");
      const activeSubKey =
        panelSubKeyByNode[nodeKey] ?? getKey(childNodes[0], "sub-0");
      const activeSubGroup =
        childNodes.find(
          (c: any, i: number) => getKey(c, `sub-${i}`) === activeSubKey
        ) || childNodes[0];
      const dropdownOptions = childNodes.map((c: any, i: number) => ({
        label: c.label,
        value: getKey(c, `sub-${i}`)
      }));
      const selectedDropdown =
        dropdownOptions.find(o => o.value === activeSubKey) ||
        dropdownOptions[0] ||
        null;
      return (
        <>
          <div className="size_subnav">
            <div className="size_format_dropdown">
              <Dropdown
                data={dropdownOptions}
                selectedItem={selectedDropdown as DropdownItemProps}
                handleChange={item => {
                  const newKey = (item?.value as string) ?? "";
                  lastUserSelectedFormatByNodeRef.current = {
                    ...lastUserSelectedFormatByNodeRef.current,
                    [nodeKey]: newKey
                  };
                  setPanelSubKeyByNode(prev => ({
                    ...prev,
                    [nodeKey]: newKey
                  }));
                }}
              />
            </div>
          </div>
          {renderPanel(activeSubGroup)}
        </>
      );
    }

    if (layout === "tabs") {
      const nodeKey = getKey(node, "");
      const activeSubKey =
        panelSubKeyByNode[nodeKey] ??
        (children[0] ? getKey(children[0], "sub-0") : null);
      const activeSubGroup =
        children.find(
          (c: any, i: number) => getKey(c, `sub-${i}`) === activeSubKey
        ) || children[0];

      if (children.length === 1) {
        const subLabel = activeSubGroup?.label;
        return (
          <>
            {subLabel && (
              <div className="size_section_sublabel">{subLabel}</div>
            )}
            {renderPanel(activeSubGroup)}
          </>
        );
      }

      return (
        <>
          <div className="size_subnav">
            <div className="size_subnav_row">
              {children.map((c: any, i: number) => {
                const key = getKey(c, `sub-${i}`);
                return (
                  <button
                    key={key}
                    type="button"
                    className={cn("size_tab_button", {
                      active: key === activeSubKey
                    })}
                    onClick={() =>
                      setPanelSubKeyByNode(prev => ({
                        ...prev,
                        [nodeKey]: key
                      }))
                    }
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
          {renderPanel(activeSubGroup)}
        </>
      );
    }

    if (layout === "sections") {
      const sectionNodes = children.filter((c: any) => c.children?.length > 0);
      const leafChips = children.filter(
        (c: any) => !c.children?.length
      ) as SingleSize[];
      return (
        <>
          {leafChips.length > 0 && (
            <div className="size_items_grid">{renderSizeChips(leafChips)}</div>
          )}
          {sectionNodes.map((section: any, i: number) => {
            const label = section.label || section.defaultValue;
            return (
              <div key={section.key || `section-${i}`} className="size_section">
                {label && <div className="size_section_label">{label}</div>}
                {renderPanel(section)}
              </div>
            );
          })}
        </>
      );
    }
    return (
      <div className="size_items_grid">
        {renderSizeChips(getLeaves(children))}
      </div>
    );
  };

  const renderSimplePanel = () => {
    if (!activeGroup) return null;
    return <div className="size_panel">{renderPanel(activeGroup)}</div>;
  };

  const renderAdvancedPanel = () => {
    const selectedFormat =
      formatOptions.find(
        (option: { label: string; value: string }) =>
          option.value === activeFormatKey
      ) ||
      formatOptions[0] ||
      null;

    return (
      <div className="size_panel">
        {styles?.length > 0 && (
          <div className="size_subnav">
            <div className="size_subnav_row">
              {styles.map((style: any, idx: number) => {
                const key = getKey(style, `style-${idx}`);
                const count = styleCounts[key] || 0;

                return (
                  <button
                    key={key}
                    type="button"
                    className={cn("size_tab_button", {
                      active: key === activeStyleKey
                    })}
                    onClick={() => setActiveStyleKey(key)}
                  >
                    <span>{style.label}</span>
                    {!!count && (
                      <span className="size_count_badge">({count})</span>
                    )}
                  </button>
                );
              })}
            </div>

            {formatOptions.length > 0 && (
              <div className="size_format_dropdown">
                <Dropdown
                  data={formatOptions}
                  selectedItem={selectedFormat as DropdownItemProps}
                  handleChange={item =>
                    setActiveFormatKey((item?.value as string) || null)
                  }
                />
              </div>
            )}
          </div>
        )}

        <div className="size_items_grid">
          {visibleSizesAdvanced?.map(size => (
            <button
              type="button"
              key={size.key}
              className={cn("size_chip", {
                active: checkIfActive(size, selectedSizes)
              })}
              onClick={() => toggleSize(size)}
            >
              {size.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <FilterElement
      className={cn("size_filter", { items_selected: !!selectedSizesCount })}
      filterLabel={filter.label}
      filterSelectedCount={selectedSizesCount}
      filterWrapperClassName="size_chart_wrapper padding0"
      onClose={resetSizeFilterNavigation}
      onOpen={focusFirstSelectedNavigation}
    >
      <div className="size_filter_layout" onWheel={handleWheelNavigate}>
        <div className="size_menu">
          {groups.map((group: any, groupIdx: number) => {
            const groupKey = getKey(group, `group-${groupIdx}`);
            return (
              <div
                key={groupKey}
                className={cn("size_menu_item", {
                  active: groupKey === activeGroupKey
                })}
                onClick={() => setActiveGroupKey(groupKey)}
              >
                <span data-label={group.label || group.defaultValue}>
                  {group.label || group.defaultValue}
                </span>
                {!!groupCounts[groupKey] && (
                  <span className="size_menu_count">
                    {groupCounts[groupKey]}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <div className="size_content">
          {categories.length > 1 && (
            <div
              className={cn("size_category_scroller", {
                "is-dragging": isCategoryScrollerDragging
              })}
              ref={categoryScrollerRef}
              onMouseDown={handleCategoryScrollerMouseDown}
            >
              <div className="size_category_tabs">
                {categories.map((cat, idx) => {
                  const catKey = getKey(cat, `category-${idx}`);
                  return (
                    <button
                      key={catKey}
                      type="button"
                      className={cn("size_category_tab", {
                        active: catKey === activeCategoryKey
                      })}
                      onClick={() => setActiveCategoryKey(catKey)}
                    >
                      {cat.label || cat.defaultLabel}
                      {!!categoryCounts[catKey] && (
                        <span className="size_menu_count">
                          {categoryCounts[catKey]}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {mode === FilterSizeVariants.SIMPLE
            ? renderSimplePanel()
            : renderAdvancedPanel()}
        </div>
      </div>
    </FilterElement>
  );
};

export default FilterSizesAggregatedWeb;
